/**
 * Production 2D layout SSOT for slots cabinet plates + poker seat rails.
 * Edited in /admin · published to D1 · applied by client (CSS vars / seat %).
 */

export type PctBox = {
  /** left % */
  l: number;
  /** right % (distance from right edge) */
  r: number;
  /** top % */
  t: number;
  /** height % (for marquee / win / buttons) OR bottom % for reels */
  h?: number;
  b?: number;
  w?: number;
};

export type SlotsLayoutV1 = {
  version: 1;
  id: "slots";
  /** Cabinet art aspect (mesh) */
  artW: number;
  artH: number;
  marquee: { l: number; r: number; t: number; h: number };
  reels: { l: number; r: number; t: number; b: number };
  win: { l: number; r: number; t: number; h: number };
  betCircle: { l: number; t: number; s: number };
  linesCircle: { l: number; t: number; s: number };
  spin: { l: number; r: number; t: number; h: number };
  auto: { l: number; w: number; t: number; h: number };
  info: { l: number; w: number; t: number; h: number };
  lever: { r: number; t: number; w: number; h: number };
};

export type SeatLayoutPoint = {
  left: number;
  top: number;
  label: string;
};

export type TableLayoutV1 = {
  version: 1;
  id: string;
  maxPlayers: 2 | 6 | 9;
  vertical: boolean;
  seats: SeatLayoutPoint[];
};

export type LayoutBundle = {
  slots: SlotsLayoutV1;
  tables: Record<string, TableLayoutV1>;
  updatedAt?: number;
  updatedBy?: string;
};

/** Defaults match `client/src/styles/slots.css` --vars on .slots-machine */
export const DEFAULT_SLOTS_LAYOUT: SlotsLayoutV1 = {
  version: 1,
  id: "slots",
  artW: 864,
  artH: 1152,
  marquee: { l: 18, r: 18, t: 15.5, h: 6 },
  reels: { l: 16, r: 16, t: 27, b: 33 },
  win: { l: 14, r: 14, t: 69, h: 7 },
  betCircle: { l: 9, t: 83.5, s: 10.5 },
  linesCircle: { l: 21.5, t: 83.5, s: 10.5 },
  spin: { l: 35, r: 35, t: 84, h: 9 },
  auto: { l: 67, w: 12, t: 84.5, h: 8 },
  info: { l: 81, w: 12, t: 84.5, h: 8 },
  lever: { r: 2, t: 34, w: 13, h: 28 },
};

export const DEFAULT_TABLE_LAYOUTS: Record<string, TableLayoutV1> = {
  "table-2": {
    version: 1,
    id: "table-2",
    maxPlayers: 2,
    vertical: false,
    seats: [
      { left: 50, top: 97, label: "hero" },
      { left: 50, top: 3, label: "villain" },
    ],
  },
  "table-2-v": {
    version: 1,
    id: "table-2-v",
    maxPlayers: 2,
    vertical: true,
    seats: [
      { left: 50, top: 90, label: "hero-rim" },
      { left: 50, top: 7, label: "villain" },
    ],
  },

  "table-6": {
    version: 1,
    id: "table-6",
    maxPlayers: 6,
    vertical: false,
    seats: [
      { left: 50, top: 96, label: "hero" },
      { left: 6, top: 72, label: "bottom-left" },
      { left: 4, top: 36, label: "mid-left" },
      { left: 22, top: 4, label: "top-left" },
      { left: 78, top: 4, label: "top-right" },
      { left: 96, top: 36, label: "mid-right" },
    ],
  },
  "table-6-v": {
    version: 1,
    id: "table-6-v",
    maxPlayers: 6,
    vertical: true,
    seats: [
      { left: 50, top: 90, label: "hero-rim" },
      { left: 14, top: 74, label: "lower-left" },
      { left: 6, top: 40, label: "mid-left" },
      { left: 50, top: 6, label: "top" },
      { left: 94, top: 40, label: "mid-right" },
      { left: 86, top: 74, label: "lower-right" },
    ],
  },

  "table-9": {
    version: 1,
    id: "table-9",
    maxPlayers: 9,
    vertical: false,
    seats: [
      { left: 50, top: 96, label: "hero" },
      { left: 18, top: 88, label: "bottom-left" },
      { left: 82, top: 88, label: "bottom-right" },
      { left: 4, top: 52, label: "mid-left" },
      { left: 96, top: 52, label: "mid-right" },
      { left: 12, top: 12, label: "top-left" },
      { left: 50, top: 2, label: "top-center" },
      { left: 88, top: 12, label: "top-right" },
      { left: 32, top: 92, label: "near-hero" },
    ],
  },
  "table-9-v": {
    version: 1,
    id: "table-9-v",
    maxPlayers: 9,
    vertical: true,
    seats: [
      { left: 50, top: 90, label: "hero-rim" },
      { left: 16, top: 78, label: "lower-left" },
      { left: 84, top: 78, label: "lower-right" },
      { left: 5, top: 50, label: "mid-left" },
      { left: 95, top: 50, label: "mid-right" },
      { left: 16, top: 18, label: "upper-left" },
      { left: 50, top: 5, label: "top" },
      { left: 84, top: 18, label: "upper-right" },
      { left: 32, top: 86, label: "near-hero" },
    ],
  },
};


export function defaultLayoutBundle(): LayoutBundle {
  return {
    slots: { ...DEFAULT_SLOTS_LAYOUT },
    tables: { ...DEFAULT_TABLE_LAYOUTS },
  };
}

export function tableLayoutKey(maxPlayers: number, vertical: boolean): string {
  const n = maxPlayers <= 2 ? 2 : maxPlayers >= 9 ? 9 : 6;
  return vertical ? `table-${n}-v` : `table-${n}`;
}

/** Map slots layout → CSS custom properties for `.slots-machine` */
export function slotsLayoutToCssVars(
  layout: SlotsLayoutV1,
): Record<string, string> {
  const s = layout;
  return {
    "--art-w": String(s.artW),
    "--art-h": String(s.artH),
    "--mq-l": `${s.marquee.l}%`,
    "--mq-r": `${s.marquee.r}%`,
    "--mq-t": `${s.marquee.t}%`,
    "--mq-h": `${s.marquee.h}%`,
    "--rw-l": `${s.reels.l}%`,
    "--rw-r": `${s.reels.r}%`,
    "--rw-t": `${s.reels.t}%`,
    "--rw-b": `${s.reels.b}%`,
    "--wr-l": `${s.win.l}%`,
    "--wr-r": `${s.win.r}%`,
    "--wr-t": `${s.win.t}%`,
    "--wr-h": `${s.win.h}%`,
    "--c1-l": `${s.betCircle.l}%`,
    "--c1-t": `${s.betCircle.t}%`,
    "--c1-s": `${s.betCircle.s}%`,
    "--c2-l": `${s.linesCircle.l}%`,
    "--c2-t": `${s.linesCircle.t}%`,
    "--c2-s": `${s.linesCircle.s}%`,
    "--sp-l": `${s.spin.l}%`,
    "--sp-r": `${s.spin.r}%`,
    "--sp-t": `${s.spin.t}%`,
    "--sp-h": `${s.spin.h}%`,
    "--au-l": `${s.auto.l}%`,
    "--au-w": `${s.auto.w}%`,
    "--au-t": `${s.auto.t}%`,
    "--au-h": `${s.auto.h}%`,
    "--in-l": `${s.info.l}%`,
    "--in-w": `${s.info.w}%`,
    "--in-t": `${s.info.t}%`,
    "--in-h": `${s.info.h}%`,
    "--lv-r": `${s.lever.r}%`,
    "--lv-t": `${s.lever.t}%`,
    "--lv-w": `${s.lever.w}%`,
    "--lv-h": `${s.lever.h}%`,
  };
}

export function clampPct(n: number, min = 0, max = 100): number {
  const x = Number(n);
  if (!Number.isFinite(x)) return min;
  return Math.min(max, Math.max(min, Math.round(x * 10) / 10));
}

export function sanitizeSlotsLayout(raw: unknown): SlotsLayoutV1 {
  const d = DEFAULT_SLOTS_LAYOUT;
  const o = (raw && typeof raw === "object" ? raw : {}) as Partial<SlotsLayoutV1>;
  const num = (v: unknown, fb: number) =>
    Number.isFinite(Number(v)) ? clampPct(Number(v), -5, 105) : fb;
  return {
    version: 1,
    id: "slots",
    artW: Number(o.artW) > 0 ? Number(o.artW) : d.artW,
    artH: Number(o.artH) > 0 ? Number(o.artH) : d.artH,
    marquee: {
      l: num(o.marquee?.l, d.marquee.l),
      r: num(o.marquee?.r, d.marquee.r),
      t: num(o.marquee?.t, d.marquee.t),
      h: num(o.marquee?.h, d.marquee.h),
    },
    reels: {
      l: num(o.reels?.l, d.reels.l),
      r: num(o.reels?.r, d.reels.r),
      t: num(o.reels?.t, d.reels.t),
      b: num(o.reels?.b, d.reels.b),
    },
    win: {
      l: num(o.win?.l, d.win.l),
      r: num(o.win?.r, d.win.r),
      t: num(o.win?.t, d.win.t),
      h: num(o.win?.h, d.win.h),
    },
    betCircle: {
      l: num(o.betCircle?.l, d.betCircle.l),
      t: num(o.betCircle?.t, d.betCircle.t),
      s: num(o.betCircle?.s, d.betCircle.s),
    },
    linesCircle: {
      l: num(o.linesCircle?.l, d.linesCircle.l),
      t: num(o.linesCircle?.t, d.linesCircle.t),
      s: num(o.linesCircle?.s, d.linesCircle.s),
    },
    spin: {
      l: num(o.spin?.l, d.spin.l),
      r: num(o.spin?.r, d.spin.r),
      t: num(o.spin?.t, d.spin.t),
      h: num(o.spin?.h, d.spin.h),
    },
    auto: {
      l: num(o.auto?.l, d.auto.l),
      w: num(o.auto?.w, d.auto.w),
      t: num(o.auto?.t, d.auto.t),
      h: num(o.auto?.h, d.auto.h),
    },
    info: {
      l: num(o.info?.l, d.info.l),
      w: num(o.info?.w, d.info.w),
      t: num(o.info?.t, d.info.t),
      h: num(o.info?.h, d.info.h),
    },
    lever: {
      r: num(o.lever?.r, d.lever.r),
      t: num(o.lever?.t, d.lever.t),
      w: num(o.lever?.w, d.lever.w),
      h: num(o.lever?.h, d.lever.h),
    },
  };
}

export function sanitizeTableLayout(
  raw: unknown,
  fallbackKey: string,
): TableLayoutV1 {
  const fb =
    DEFAULT_TABLE_LAYOUTS[fallbackKey] || DEFAULT_TABLE_LAYOUTS["table-6"]!;
  const o = (raw && typeof raw === "object" ? raw : {}) as Partial<TableLayoutV1>;
  const seatsIn = Array.isArray(o.seats) ? o.seats : fb.seats;
  const seats = seatsIn.map((s, i) => ({
    left: clampPct(Number((s as SeatLayoutPoint).left ?? fb.seats[i]?.left ?? 50)),
    top: clampPct(Number((s as SeatLayoutPoint).top ?? fb.seats[i]?.top ?? 50)),
    label: String((s as SeatLayoutPoint).label || fb.seats[i]?.label || `seat-${i}`),
  }));
  const maxPlayers = ([2, 6, 9].includes(Number(o.maxPlayers))
    ? Number(o.maxPlayers)
    : fb.maxPlayers) as 2 | 6 | 9;
  return {
    version: 1,
    id: String(o.id || fallbackKey),
    maxPlayers,
    vertical: Boolean(o.vertical ?? fb.vertical),
    seats:
      seats.length >= maxPlayers
        ? seats.slice(0, maxPlayers)
        : [
            ...seats,
            ...fb.seats.slice(seats.length, maxPlayers),
          ],
  };
}
